import { ThingEventSnapshot } from '@eagle/core-data-types';
import { EventRecordEventTypes, FeatureTypes } from '@eagle/data-function-types';
import { useMemo } from 'react';
import { useBoolFlag, useObjectFlag } from '../components/flags';
import { EventDefinition } from '../notable-events';
import { eventSummarizationTemplateValidator, ProcessedEvent, ProcessedEventType, summarizeEvents } from '../util/event-summarization';

export const useEventSummarization = (
  events: ThingEventSnapshot[],
  notableEvents: EventDefinition[],
): ProcessedEvent[] => {
  const eventSummarizationFeature = useBoolFlag('track-thing-history-event-summarisation-feature');
  const template = useObjectFlag('track-thing-history-event-summarisation-template', eventSummarizationTemplateValidator);

  return useMemo(() => {
    if (!eventSummarizationFeature) return [];

    if (!events.length || !notableEvents.length || !template) {
      return events.map((event) => ({ type: ProcessedEventType.Individual, event }));
    }

    const notableEventsOnly = events.filter((event) => {
      const { feature, featureTypeId, eventTypeId } = event;

      if (featureTypeId === FeatureTypes.EVENT_RECORD_V0 && eventTypeId === EventRecordEventTypes.OCCURRENCE) {
        return notableEvents.some((ne) =>
          ne.enabled &&
          ne.notableEvent &&
          feature.startsWith(ne.featureId) &&
          eventTypeId === ne.eventTypeId,
        );
      }

      return notableEvents.some((ne) =>
        ne.enabled &&
        ne.notableEvent &&
        ne.featureId === featureTypeId &&
        ne.eventTypeId === eventTypeId,
      );
    });

    if (notableEventsOnly.length === 0) {
      return events.map((event) => ({ type: ProcessedEventType.Individual, event }));
    }

    return summarizeEvents(notableEventsOnly, template);
  }, [events, notableEvents, template, eventSummarizationFeature]);
};
